import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, fetchApartmentContent } from "@/api/client";
import { useGuestAuth } from "@/context/GuestAuthContext";
import { pickLocale } from "@/lib/helpers";
import { track } from "@/api/analytics";
import ContentBlocks from "@/components/ContentBlocks";
import * as Icons from "lucide-react";
import { Loader2, LogOut } from "lucide-react";

export default function ApartmentDetail() {
  const { slug } = useParams();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { logout, isUnlocked } = useGuestAuth();
  const [apt, setApt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // First, list apartments to find id by slug
        const { data: list } = await api.get("/public/apartments");
        const stub = list.find((a) => a.slug === slug);
        if (!stub) {
          setErr("not_found");
          return;
        }
        if (!isUnlocked(stub.id)) {
          // Redirect back to home for unlock
          navigate("/");
          return;
        }
        const full = await fetchApartmentContent(stub.id);
        if (!cancelled) {
          setApt(full);
          track("view_apartment", { target_type: "apartment", target_id: full.id });
        }
      } catch (e) {
        setErr("unauthorized");
        navigate("/");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug, isUnlocked, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-ink-300">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (!apt) return null;

  const Icon = Icons[apt.icon] || Icons.Home;

  const handleLogout = () => {
    logout(apt.id);
    navigate("/");
  };

  return (
    <div className="px-5 pt-4">
      <div className="text-center pt-4 animate-fade-up">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-bronze-50 flex items-center justify-center">
          <Icon strokeWidth={1.4} className="w-5 h-5 text-bronze-500" />
        </div>
        <h1
          data-testid="apartment-title"
          className="font-editorial italic text-4xl text-ink-900 mt-3 leading-tight"
        >
          {pickLocale(apt.name, i18n.language)}
        </h1>
        <p className="text-xs uppercase font-semibold tracking-[0.16em] text-ink-300 mt-1">
          {pickLocale(apt.subtitle, i18n.language)}
        </p>
        <p
          data-testid="apartment-tagline"
          className="font-editorial italic text-[15px] text-ink-500 mt-2"
        >
          {t("tagline.apartment")}
        </p>
        <div className="flex justify-center my-3">
          <span className="ornament" />
        </div>
      </div>

      <section
        className="animate-fade-up bg-white/95 rounded-3xl border border-ink-200/40 shadow-soft p-5 mt-4"
        style={{ animationDelay: "80ms" }}
        data-testid="apartment-content"
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-500 mb-3">
          {t("apartment.reserved_content")}
        </div>
        <ContentBlocks blocks={apt.blocks || []} />
      </section>

      <button
        onClick={handleLogout}
        data-testid="apartment-logout"
        className="mt-6 mx-auto flex items-center gap-2 text-xs text-ink-300 hover:text-ink-700 transition"
      >
        <LogOut className="w-3.5 h-3.5" />
        {t("apartment.logout")}
      </button>
    </div>
  );
}
